import { Alert, FormGroup, FormSection, PasswordInput, Text, TextInput } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../utils/validation';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { translateKey } from '../../../translate';

interface IMfaLoginCredentialsInputProps {
    errorCode: ValidationErrorCode | null;
    errors: ValidationErrors;
    isFormDisabled: boolean;
    onChange: (key: 'userName' | 'password', value: string) => void;
    onDoValidate: (name: 'userName' | 'password') => void;
    password: string;
    remainingAttempts: number | null;
    submitCredentials: () => Promise<void>;
    userName: string;
}

export function MfaLoginCredentialsInput(props: IMfaLoginCredentialsInputProps) {
    return (
        <>
            {showErrorMessage()}
            <Text>Log alsjeblieft eerst in</Text>
            <FormSection>
                <FormGroup
                    hasErrors={props.errors.has('userName')}
                    label="Gebruikersnaam"
                    layout="vertical"
                >
                    <TextInput
                        disabled
                        error={
                            props.errors.get('userName')
                                ? translateKey('validation.' + props.errors.get('userName'))
                                : undefined
                        }
                        name="userName"
                        onBlur={() => props.onDoValidate('userName')}
                        onChange={(e) => props.onChange('userName', e.target.value)}
                        onEnterKeyPress={async () => await props.submitCredentials()}
                        type="text"
                        value={props.userName}
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={props.errors.has('password')}
                    label="Wachtwoord"
                    layout="vertical"
                >
                    <PasswordInput
                        disabled={props.isFormDisabled}
                        error={
                            props.errors.get('password')
                                ? translateKey('validation.' + props.errors.get('password'))
                                : undefined
                        }
                        name="password"
                        onBlur={() => props.onDoValidate('password')}
                        onChange={(e) => props.onChange('password', e.target.value)}
                        onEnterKeyPress={async () => await props.submitCredentials()}
                        value={props.password}
                    />
                </FormGroup>
            </FormSection>
        </>
    );

    function showErrorMessage() {
        if (!props.errorCode) {
            return null;
        } else if (props.errorCode === ValidationErrorCode.WRONG_PASSWORD_WARNING) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    {`De combinatie van wachtwoord en gebruikersnaam klopt niet. Wanneer je nog ${props.remainingAttempts} keer je
                        inloggegevens verkeerd invult, moet je 15 minuten wachten voordat je het opnieuw kunt proberen.`}
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USER_BLOCKED_TEMPORARILY) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Je kan niet meer inloggen voor 15 minuten. Probeer het daarna nog eens. Hulp nodig?{' '}
                    <Alert.Link
                        href="https://www.ksyos.nl/contact/faq"
                        openInNewTab
                        variant="externalLink"
                    >
                        {' '}
                        Lees onze FAQ hier
                    </Alert.Link>
                </Alert>
            );
        } else {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Er is bij ons iets verkeerd gegaan. Probeer het later opnieuw, of laat ons je verder helpen. Bel{' '}
                    <Alert.Link
                        href="tel:+31 20 600 00 60"
                        variant="externalLink"
                    >
                        020-600 00 60
                    </Alert.Link>{' '}
                    of mail{' '}
                    <Alert.Link
                        href="mailto:info@ksyos.nl"
                        variant="externalLink"
                    >
                        info@ksyos.nl
                    </Alert.Link>
                    .
                </Alert>
            );
        }
    }
}
